import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const StoreCartContext = createContext(null);
const STORAGE_KEY = 'wergame-store-cart-v1';

function loadCart() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function cartLineKey(productId, variantId) {
  return `${productId}:${variantId || 'default'}`;
}

export function StoreCartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, variant = null, quantity = 1) => {
    if (!product?._id) return;
    const key = cartLineKey(product._id, variant?._id);
    const maxStock = product.trackInventory
      ? Math.max(0, variant ? variant.stock || 0 : product.stock || 0)
      : 25;
    setItems((current) => {
      if (product.trackInventory && maxStock <= 0) return current;
      const existing = current.find((item) => item.key === key);
      const nextQuantity = Math.min(
        25,
        product.trackInventory ? maxStock : 25,
        (existing?.quantity || 0) + quantity
      );
      const snapshot = {
        key,
        productId: product._id,
        slug: product.slug,
        name: product.name,
        image: product.images?.[0]?.url || '',
        priceGolden: product.priceGolden,
        quantity: Math.max(1, nextQuantity),
        variantId: variant?._id || null,
        variantName: variant?.name || '',
        maxStock,
      };
      if (existing) return current.map((item) => (item.key === key ? snapshot : item));
      return [...current, snapshot];
    });
  }, []);

  const updateQuantity = useCallback((key, quantity) => {
    setItems((current) =>
      current
        .map((item) =>
          item.key === key
            ? {
                ...item,
                quantity: Math.min(
                  25,
                  item.maxStock > 0 ? item.maxStock : 25,
                  Math.max(0, parseInt(quantity, 10) || 0)
                ),
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((current) => current.filter((item) => item.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const totalGolden = useMemo(
    () => items.reduce((sum, item) => sum + item.priceGolden * item.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      itemCount,
      totalGolden,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
    }),
    [items, itemCount, totalGolden, addItem, updateQuantity, removeItem, clearCart]
  );

  return <StoreCartContext.Provider value={value}>{children}</StoreCartContext.Provider>;
}

export function useStoreCart() {
  const context = useContext(StoreCartContext);
  if (!context) throw new Error('useStoreCart must be used within StoreCartProvider');
  return context;
}
